import React, { useState, useEffect, useRef } from 'react';
import { registry } from '../registry';
import { RsvpForm } from '@/src/modules/rsvp';
import type { InvitationData, TemplateMetadata } from '../types';
import { motion, AnimatePresence } from 'motion/react';
import { Icons } from '@/src/modules/components/Icons';
import { transliterateToMarathi } from '@/src/infrastructure/lib/transliterate';
import { extractCoupleNames, parseFlexibleDate } from '@/src/infrastructure/lib/weddingDataHelper';

const META: TemplateMetadata = {
  id: 'marathi-wedding-interactive',
  name: 'Marathi Wedding Interactive',
  occasionType: 'WEDDING',
  description: 'Premium bilingual Marathi wedding card with falling marigold petals, glowing lanterns, and 3D parallax effects.',
  isPremium: true,
  tags: ['marathi', 'traditional', '3d', 'interactive', 'animated', 'premium'],
  colorScheme: { primary: '#B91C1C', accent: '#D97706', bg: '#FFFDE7' },
};

// Fallback translations map for static UI texts
const TRANSLATIONS = {
  en: {
    invitationGreeting: 'S.N.Vi.Vi. We cordially invite you to join us on the auspicious occasion of the wedding of',
    invitationTitle: 'Wedding Ceremony',
    concludesWith: 'We seek your blessings and presence.',
    haldiTitle: 'Haldi Ceremony',
    venueTitle: 'Venue',
    hostsTitle: 'Invited By',
    attendingText: 'Attending',
    notAttendingText: 'Unable to attend',
    rsvpBtn: 'RSVP Now',
    musicOn: 'Sound On',
    musicOff: 'Sound Off',
  },
  mr: {
    invitationGreeting: 'स.न.वि.वि. आमच्या येथे खालील शुभमुहूर्तावर विवाह सोहळा करण्याचे योजिले आहे. तरी या कार्यास आपली उपस्थिती प्रार्थनीय आहे.',
    invitationTitle: 'शुभविवाह सोहळा',
    concludesWith: 'आपले स्नेहांकित व शुभाशीर्वाद.',
    haldiTitle: 'हळद समारंभ',
    venueTitle: 'विवाहस्थळ',
    hostsTitle: 'निमंत्रक',
    attendingText: 'उपस्थित राहणार',
    notAttendingText: 'असमर्थ',
    rsvpBtn: 'आर.एस.व्ही.पी.',
    musicOn: 'संगीत सुरू',
    musicOff: 'संगीत बंद',
  }
};

interface Petal {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number;
  rotation: number;
  speedX: number;
}

// Traditional hanging Indian wedding lantern (Akash Kandil) inline SVG component
const AkashKandil = () => (
  <svg viewBox="0 0 100 220" className="w-20 h-44 md:w-28 md:h-60 filter drop-shadow-[0_6px_16px_rgba(251,191,36,0.65)]">
    {/* Hanging Thread */}
    <line x1="50" y1="0" x2="50" y2="40" stroke="#D97706" strokeWidth="2.5" />
    
    {/* Top Cap */}
    <path d="M40 40 L60 40 L50 30 Z" fill="#B91C1C" />
    
    {/* Main Body (Diamond Frame) */}
    <polygon points="50,40 85,80 50,120 15,80" fill="#D97706" stroke="#FEF3C7" strokeWidth="2.5" />
    <polygon points="50,50 73,80 50,110 27,80" fill="#B91C1C" />
    <polygon points="50,60 63,80 50,100 37,80" fill="#FEF3C7" opacity="0.9" />
    
    {/* Tassels / Streamers */}
    <path d="M22 108 C15 150 20 190 18 210" stroke="#D97706" strokeWidth="3" strokeLinecap="round" fill="none" />
    <path d="M36 114 C30 155 35 195 32 215" stroke="#B91C1C" strokeWidth="3.5" strokeLinecap="round" fill="none" />
    <path d="M50 120 C50 160 52 200 50 220" stroke="#FEF3C7" strokeWidth="4" strokeLinecap="round" fill="none" />
    <path d="M64 114 C70 155 65 195 68 215" stroke="#B91C1C" strokeWidth="3.5" strokeLinecap="round" fill="none" />
    <path d="M78 108 C85 150 80 190 82 210" stroke="#D97706" strokeWidth="3" strokeLinecap="round" fill="none" />
  </svg>
);

// Traditional Ganesha minimalist line art SVG icon component
const GaneshaIcon = () => (
  <svg viewBox="0 0 100 100" className="w-12 h-12 text-[#FEF3C7]" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Head crown */}
    <path d="M50 12 L50 22 M40 18 L60 18 M44 14 L56 14" />
    {/* Ears */}
    <path d="M35 32 C18 32 22 55 35 48" />
    <path d="M65 32 C82 32 78 55 65 48" />
    {/* Face and trunk */}
    <path d="M38 28 C38 20 62 20 62 28 C62 38 46 40 46 56 C46 68 56 72 56 60 C56 54 50 54 50 60" />
    {/* Tilak */}
    <path d="M50 28 L50 36" strokeWidth="4" stroke="#D97706" />
    <path d="M46 32 L54 32" strokeWidth="2.5" stroke="#D97706" />
    {/* Modak */}
    <circle cx="66" cy="65" r="4.5" fill="#D97706" stroke="none" />
  </svg>
);

export function MarathiWeddingInteractive({ data }: { data: InvitationData }) {
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  // Set default language based on saved targetLanguage preference
  const targetLang = data.customData?.wedding?.targetLanguage || 'mr';
  const [lang, setLang] = useState<'mr' | 'en'>(targetLang);
  const [userToggled, setUserToggled] = useState(false);
  const [petals, setPetals] = useState<Petal[]>([]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userToggled) {
      setLang(data.customData?.wedding?.targetLanguage || 'mr');
    }
  }, [data.customData?.wedding?.targetLanguage, userToggled]);

  useEffect(() => {
    const arr: Petal[] = [];
    for (let i = 0; i < 25; i++) {
      arr.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 8,
        duration: 8 + Math.random() * 7,
        size: 12 + Math.random() * 16,
        rotation: Math.random() * 360,
        speedX: -20 + Math.random() * 40,
      });
    }
    setPetals(arr);

    const styleId = 'marathi-interactive-styles';
    if (!document.getElementById(styleId)) {
      const s = document.createElement('style');
      s.id = styleId;
      s.textContent = `
        @keyframes petalFall {
          0% {
            transform: translateY(-50px) rotate(0deg) translateX(0);
            opacity: 0;
          }
          10% { opacity: 0.9; }
          90% { opacity: 0.8; }
          100% {
            transform: translateY(105vh) rotate(720deg) translateX(var(--drift-x, 50px));
            opacity: 0;
          }
        }
        .animate-petal {
          animation: petalFall var(--fall-duration, 12s) linear infinite;
          animation-delay: var(--fall-delay, 0s);
        }
        @keyframes lanternSwayGrow {
          0%, 100% { transform: rotate(3deg) scale(1); }
          50% { transform: rotate(-3deg) scale(1.08); }
        }
        .animate-lantern {
          animation: lanternSwayGrow 6s ease-in-out infinite;
          transform-origin: top center;
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.8; filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.45)); }
          50% { opacity: 1.0; filter: drop-shadow(0 0 20px rgba(251, 191, 36, 0.9)); }
        }
        .animate-glow {
          animation: glowPulse 4s ease-in-out infinite;
        }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePos({ x, y });
  };

  const t = TRANSLATIONS[lang];
  const groom = data.customData?.wedding?.groom;
  const bride = data.customData?.wedding?.bride;
  const parsedCouple = extractCoupleNames(data.eventName);

  // Helper function to transliterate user input when Marathi is toggled
  const maybeTransliterate = (text: string | undefined, defaultMr: string, defaultEn: string): string => {
    if (lang === 'mr') {
      return text ? transliterateToMarathi(text) : defaultMr;
    }
    return text || defaultEn;
  };

  const getGroomName = () => {
    const rawName = groom?.nickname || groom?.fullName || parsedCouple?.groom;
    if (lang === 'mr') {
      return rawName ? `चि. ${transliterateToMarathi(rawName)}` : 'चि. वर';
    }
    return rawName ? `Ch. ${rawName}` : 'Ch. Groom';
  };

  const getBrideName = () => {
    const rawName = bride?.nickname || bride?.fullName || parsedCouple?.bride;
    if (lang === 'mr') {
      return rawName ? `चि.सौ.कां. ${transliterateToMarathi(rawName)}` : 'चि.सौ.कां. वधू';
    }
    return rawName ? `Chi.Sou.Kan. ${rawName}` : 'Chi.Sou.Kan. Bride';
  };

  const getGroomParents = () => {
    const father = groom?.fatherName;
    const mother = groom?.motherName;
    const family = groom?.family;
    const address = groom?.homeAddress;
    
    if (groom?.parents) {
      return lang === 'mr' ? `${transliterateToMarathi(groom.parents)}\nयांचे चिरंजीव` : `Son of ${groom.parents}`;
    }

    if (father) {
      const motherPrefixMr = mother ? `सौ. ${transliterateToMarathi(mother)} व ` : '';
      const fatherPrefixMr = `श्री. ${transliterateToMarathi(father)}`;
      const familyStrMr = family ? ` ${transliterateToMarathi(family)}` : '';
      const addrStrMr = address ? `\nरा. ${transliterateToMarathi(address)}` : '';
      
      const motherPrefixEn = mother ? `Mrs. ${mother} & ` : '';
      const fatherPrefixEn = `Mr. ${father}`;
      const familyStrEn = family ? ` ${family}` : '';
      const addrStrEn = address ? `\nof ${address}` : '';
      
      if (lang === 'mr') {
        return `${motherPrefixMr}${fatherPrefixMr}${familyStrMr}${addrStrMr}\nयांचे चिरंजीव`;
      }
      return `Son of ${motherPrefixEn}${fatherPrefixEn}${familyStrEn}${addrStrEn}`;
    }
    
    if (data.hostName) {
      return lang === 'mr' 
        ? `निमंत्रक:\n${transliterateToMarathi(data.hostName)}\nव समस्त परिवार` 
        : `Host:\n${data.hostName}\n& Family`;
    }
    return '';
  };

  const getBrideParents = () => {
    const father = bride?.fatherName;
    const mother = bride?.motherName;
    const family = bride?.family;
    const address = bride?.homeAddress;
    
    if (bride?.parents) {
      return lang === 'mr' ? `${transliterateToMarathi(bride.parents)}\nयांची कन्या` : `Daughter of ${bride.parents}`;
    }

    if (father) {
      const motherPrefixMr = mother ? `सौ. ${transliterateToMarathi(mother)} व ` : '';
      const fatherPrefixMr = `श्री. ${transliterateToMarathi(father)}`;
      const familyStrMr = family ? ` ${transliterateToMarathi(family)}` : '';
      const addrStrMr = address ? `\nरा. ${transliterateToMarathi(address)}` : '';
      
      const motherPrefixEn = mother ? `Mrs. ${mother} & ` : '';
      const fatherPrefixEn = `Mr. ${father}`;
      const familyStrEn = family ? ` ${family}` : '';
      const addrStrEn = address ? `\nof ${address}` : '';
      
      if (lang === 'mr') {
        return `${motherPrefixMr}${fatherPrefixMr}${familyStrMr}${addrStrMr}\nयांची कन्या`;
      }
      return `Daughter of ${motherPrefixEn}${fatherPrefixEn}${familyStrEn}${addrStrEn}`;
    }
    
    if (data.hostName) {
      return lang === 'mr' 
        ? `निमंत्रक:\n${transliterateToMarathi(data.hostName)}\nव समस्त परिवार` 
        : `Host:\n${data.hostName}\n& Family`;
    }
    return '';
  };

  const parsedEventDate = parseFlexibleDate(data.eventDate);

  const getWeddingDate = () => {
    if (!parsedEventDate) {
      return lang === 'mr' 
        ? 'शुभविवाह दिनांक लवकरच जाहीर होईल' 
        : 'Wedding date to be announced';
    }
    try {
      const d = parsedEventDate;
      if (lang === 'mr') {
        const weekdays = ['रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];
        const monthsMr = ['जानेवारी', 'फेब्रुवारी', 'मार्च', 'एप्रिल', 'मे', 'जून', 'जुलै', 'ऑगस्ट', 'सप्टेंबर', 'ऑक्टोबर', 'नोव्हेंबर', 'डिसेंबर'];
        const hours = d.getHours();
        const minutes = d.getMinutes().toString().padStart(2, '0');
        const period = hours >= 12 ? 'दुपारी' : 'सकाळी';
        const displayHours = hours % 12 || 12;
        const timeStr = `${period} ${displayHours}:${minutes} वाजता`;
        return `${weekdays[d.getDay()]} दि. ${d.getDate()} ${monthsMr[d.getMonth()]} ${d.getFullYear()}\nरोजी ${timeStr}`;
      }
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }) + `\nat ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } catch {
      return '';
    }
  };

  const getVenue = () => {
    return maybeTransliterate(data.eventVenue, 'विवाह स्थळ', 'Wedding Venue');
  };

  const getHosts = () => {
    if (data.hostName) {
      return lang === 'mr' 
        ? `निमंत्रक:\n${transliterateToMarathi(data.hostName)}\nव समस्त परिवार` 
        : `Invited by:\n${data.hostName}\n& Family`;
    }
    const familyFallback = groom.fullName || bride.fullName
      ? (lang === 'mr' ? `${transliterateToMarathi(groom.fullName || bride.fullName)} व समस्त परिवार` : `${groom.fullName || bride.fullName} & Family`)
      : (lang === 'mr' ? 'समस्त परिवार' : 'Family & Well-wishers');
    return lang === 'mr' 
      ? `निमंत्रक:\n${familyFallback}`
      : `Invited by:\n${familyFallback}`;
  };

  const formatEventDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const d = parseFlexibleDate(dateStr);
      if (!d) return dateStr;
      if (lang === 'mr') {
        const weekdays = ['रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];
        const monthsMr = ['जानेवारी', 'फेब्रुवारी', 'मार्च', 'एप्रिल', 'मे', 'जून', 'जुलै', 'ऑगस्ट', 'सप्टेंबर', 'ऑक्टोबर', 'नोव्हेंबर', 'डिसेंबर'];
        return `${weekdays[d.getDay()]} दि. ${d.getDate()} ${monthsMr[d.getMonth()]} ${d.getFullYear()}`;
      }
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const effectiveDate = parsedEventDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const prevDate = new Date(effectiveDate.getTime() - 24 * 60 * 60 * 1000);
  const defaultVenue = data.eventVenue || (lang === 'mr' ? 'विवाह स्थळ' : 'Wedding Venue');

  // Resolve events list from inputs or fallbacks
  const rawCeremonies = data.customData?.wedding?.ceremonies || data.customData?.wedding?.weddingEvents;
  const events: Array<{
    title: string;
    date: string;
    startTime?: string;
    venue?: string;
    description?: string;
    coverImageUrl?: string;
  }> = (Array.isArray(rawCeremonies) && rawCeremonies.length > 0)
    ? rawCeremonies.map((c: any) => ({
        title: lang === 'mr' ? transliterateToMarathi(c.nameMr || c.name || c.title || 'कार्यक्रम') : (c.nameEn || c.name || c.title || 'Ceremony'),
        date: c.date || effectiveDate.toISOString(),
        startTime: c.time || c.startTime || '12:45 PM',
        venue: lang === 'mr' ? transliterateToMarathi(c.venue || defaultVenue) : (c.venue || defaultVenue),
        description: c.note || c.description || '',
        coverImageUrl: c.coverImageUrl,
      }))
    : [
        {
          title: lang === 'mr' ? 'हळद समारंभ' : 'Haldi Ceremony',
          date: prevDate.toISOString(),
          startTime: '06:00 PM',
          venue: lang === 'mr' ? transliterateToMarathi(defaultVenue) : defaultVenue,
          description: lang === 'mr' ? 'हळदीचा सोहळा आणि संगीत रजनी.' : 'Henna and music celebrations.',
        },
        {
          title: lang === 'mr' ? 'शुभविवाह सोहळा' : 'Wedding Ceremony',
          date: effectiveDate.toISOString(),
          startTime: '12:45 PM',
          venue: lang === 'mr' ? transliterateToMarathi(defaultVenue) : defaultVenue,
          description: lang === 'mr' ? 'मंगलाष्टक आणि शुभविवाह सोहळा.' : 'Auspicious wedding ceremony followed by lunch.',
        }
      ];

  // Resolve pre-wedding shoot media items
  const mediaItems = data.customData?.wedding?.preWeddingStory?.moments || data.customData?.wedding?.mediaShowcase || [];
  
  // Safe partner attribution check
  const partner = (data as any).partnerAttribution || data.customData?.partnerAttribution;

  return (
    <div 
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen bg-[#FDFBF7] text-[#3F2E21] overflow-hidden flex flex-col items-center py-12 px-4 select-none"
      style={{ 
        fontFamily: "'Cinzel', 'Georgia', serif",
        perspective: '1000px'
      }}
    >
      {/* 3D Parallax Background Backdrop */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-transform duration-300 ease-out pointer-events-none opacity-65 z-0 scale-105"
        style={{ 
          backgroundImage: "url('/assets/marathi/backdrop.jpg')",
          transform: `translate3d(${mousePos.x * -30}px, ${mousePos.y * -30}px, 0)` 
        }}
      />

      {/* Falling Marigold Petals Layer */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-25">
        {petals.map((p) => (
          <div
            key={p.id}
            className="absolute animate-petal opacity-0"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              backgroundColor: Math.random() > 0.4 ? '#FBBF24' : '#F59E0B',
              borderRadius: '50% 0 50% 50%',
              transform: `rotate(${p.rotation}deg)`,
              '--fall-delay': `${p.delay}s`,
              '--fall-duration': `${p.duration}s`,
              '--drift-x': `${p.speedX}px`,
            } as React.CSSProperties}
          />
        ))}
      </div>

      {/* Floating Control Panel (Bilingual Switch) */}
      <div className="fixed top-4 right-4 z-40 flex items-center gap-2">
        <button
          data-testid="bilingual-toggle"
          onClick={() => {
            console.log('CLICKED BUTTON, CURRENT LANG:', lang);
            setUserToggled(true);
            setLang(lang === 'mr' ? 'en' : 'mr');
          }}
          className="px-4 py-2 rounded-full bg-white/70 backdrop-blur-md border border-white/50 shadow-lg hover:bg-white/95 transition-all text-xs font-bold uppercase tracking-widest text-[#B91C1C]"
        >
          {lang === 'mr' ? 'English' : 'मराठी'}
        </button>
      </div>

      {/* Swaying & Glowing Hanging SVG Lanterns (Akash Kandils) */}
      <div className="absolute top-0 inset-x-0 flex justify-between px-4 md:px-16 pointer-events-none z-10">
        <div className="animate-lantern animate-glow transform origin-top">
          <AkashKandil />
        </div>
        <div className="animate-lantern animate-glow transform origin-top scale-x-[-1]">
          <AkashKandil />
        </div>
      </div>

      {/* Ganesha / Title Section */}
      <motion.div 
        initial={{ opacity: 0, y: -25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="relative z-10 flex flex-col items-center text-center mt-16 max-w-lg"
      >
        <div className="w-20 h-20 bg-gradient-to-br from-[#B91C1C] to-[#991B1B] rounded-full flex items-center justify-center text-[#FEF3C7] shadow-xl mb-6 border-2 border-[#D97706]/40">
          <GaneshaIcon />
        </div>
        <span className="text-sm tracking-[0.25em] text-[#B91C1C] font-bold uppercase mb-2">
          {lang === 'mr' ? '|| श्री गणेशाय नमः ||' : '|| Shri Ganeshay Namah ||'}
        </span>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#78350F] tracking-wide mt-2">
          {t.invitationTitle}
        </h1>
        <div className="w-32 h-[1px] bg-gradient-to-r from-transparent via-[#D97706] to-transparent my-4" />
        <p className="text-sm italic text-[#5C4D3C] px-6 leading-relaxed font-medium">
          {t.invitationGreeting}
        </p>
      </motion.div>

      {/* 3D Floating Details Card (Groom / Bride) */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.2 }}
        className="relative z-10 mt-10 w-full max-w-xl bg-white/75 backdrop-blur-lg border border-white/60 rounded-[32px] p-8 md:p-12 shadow-2xl flex flex-col items-center gap-6"
        style={{ 
          transform: `perspective(1000px) rotateX(${mousePos.y * -20}deg) rotateY(${mousePos.x * 20}deg) translate3d(${mousePos.x * 35}px, ${mousePos.y * 35}px, 0)`,
          transition: 'transform 0.15s ease-out'
        }}
      >
        {/* Bride Section */}
        <div className="flex flex-col items-center text-center w-full">
          {bride?.photoUrl ? (
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-[#D97706] shadow-md overflow-hidden mb-4 bg-white/80">
              <img src={bride.photoUrl} alt="Bride" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-12 h-12 text-[#D97706]/40 mb-3 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
                <path d="M12 2a6 6 0 0 0-6 6c0 4 6 14 6 14s6-10 6-6a6 6 0 0 0-6-6z" />
                <circle cx="12" cy="8" r="2" />
              </svg>
            </div>
          )}
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#B91C1C] tracking-wide mb-2">
            {getBrideName()}
          </h2>
          <p className="text-xs md:text-sm text-[#5C4D3C] whitespace-pre-line leading-relaxed font-sans font-medium">
            {getBrideParents()}
          </p>
        </div>

        {/* Peacock Decorative Separator */}
        <div 
          className="w-40 h-28 bg-contain bg-center bg-no-repeat opacity-95 my-2 drop-shadow-md hover:scale-105 transition-transform cursor-pointer"
          style={{ 
            backgroundImage: "url('/assets/marathi/peacock.jpg')"
          }}
        />

        {/* Groom Section */}
        <div className="flex flex-col items-center text-center w-full">
          {groom?.photoUrl ? (
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-[#D97706] shadow-md overflow-hidden mb-4 bg-white/80">
              <img src={groom.photoUrl} alt="Groom" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-12 h-12 text-[#D97706]/40 mb-3 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
                <path d="M12 2L2 22h20L12 2z M12 6l6 12H6L12 6z" />
              </svg>
            </div>
          )}
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#B91C1C] tracking-wide mb-2">
            {getGroomName()}
          </h2>
          <p className="text-xs md:text-sm text-[#5C4D3C] whitespace-pre-line leading-relaxed font-sans font-medium">
            {getGroomParents()}
          </p>
        </div>
      </motion.div>

      {/* Date, Time & Venue Card */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1.0 }}
        className="relative z-10 mt-8 w-full max-w-lg bg-[#FAF7F0] border border-[#E9E4D9] rounded-2xl p-6 md:p-8 shadow-lg text-center space-y-6"
        style={{ 
          transform: `perspective(1000px) rotateX(${mousePos.y * -15}deg) rotateY(${mousePos.x * 15}deg) translate3d(${mousePos.x * -20}px, ${mousePos.y * 25}px, 0)`,
          transition: 'transform 0.15s ease-out'
        }}
      >
        {/* Wedding Date/Time */}
        <div className="space-y-2">
          <span className="text-[10px] tracking-[0.2em] text-[#D97706] font-bold uppercase">
            {lang === 'mr' ? 'शुभमुहूर्त' : 'Auspicious Date & Time'}
          </span>
          <p className="text-lg md:text-xl font-bold text-[#78350F] whitespace-pre-line leading-relaxed">
            {getWeddingDate()}
          </p>
        </div>

        {/* Dynamic Wedding Events loops (Haldi, Mehendi, etc.) */}
        {events.map((event, index) => (
          <div key={index} className="border-t border-[#E9E4D9] pt-4 space-y-2">
            {event.coverImageUrl && (
              <div className="w-full h-24 rounded-lg overflow-hidden mb-2 bg-stone-100 border border-[#E9E4D9]">
                <img src={event.coverImageUrl} alt={event.title} className="w-full h-full object-cover" />
              </div>
            )}
            <span className="text-[10px] tracking-[0.2em] text-[#D97706] font-bold uppercase">
              {lang === 'mr' ? transliterateToMarathi(event.title) : event.title}
            </span>
            <p className="text-sm font-semibold text-[#5C4D3C] whitespace-pre-line leading-relaxed">
              {formatEventDate(event.date)} {event.startTime && `\n${lang === 'mr' ? transliterateToMarathi(event.startTime) : event.startTime}`}
            </p>
            {event.venue && (
              <p className="text-xs text-[#5C4D3C]/80 italic font-medium">
                {lang === 'mr' ? transliterateToMarathi(event.venue) : event.venue}
              </p>
            )}
            {event.description && (
              <p className="text-xs text-[#5C4D3C] font-sans leading-relaxed">
                {lang === 'mr' ? transliterateToMarathi(event.description) : event.description}
              </p>
            )}
          </div>
        ))}

        {/* Venue Address */}
        <div className="border-t border-[#E9E4D9] pt-4 space-y-2">
          <span className="text-[10px] tracking-[0.2em] text-[#D97706] font-bold uppercase">
            {t.venueTitle}
          </span>
          <p className="text-sm font-semibold text-[#5C4D3C] whitespace-pre-line leading-relaxed font-sans">
            {getVenue()}
          </p>
        </div>
      </motion.div>

      {/* Pre-Wedding Photo Gallery Grid */}
      {mediaItems.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.0 }}
          className="relative z-10 mt-8 w-full max-w-xl bg-white/60 backdrop-blur-md border border-[#E9E4D9] rounded-3xl p-6 md:p-8 shadow-lg text-center space-y-6"
        >
          <span className="text-[10px] tracking-[0.2em] text-[#D97706] font-bold uppercase">
            {lang === 'mr' ? 'गोड आठवणी' : 'Pre-Wedding Moments'}
          </span>
          <h2 className="text-2xl font-bold text-[#78350F]">
            {lang === 'mr' ? 'फोटो गॅलरी' : 'Our Gallery'}
          </h2>
          <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-[#D97706] to-transparent mx-auto my-2" />
          
          <div className="grid grid-cols-2 gap-4">
            {mediaItems.map((item, index) => (
              <div 
                key={index} 
                className="overflow-hidden rounded-2xl border-2 border-[#FEF3C7] shadow-md aspect-square bg-stone-100 group relative"
              >
                {item.type === 'video' ? (
                  <video src={item.url} controls className="w-full h-full object-cover" />
                ) : (
                  <img src={item.url} alt={item.caption || 'Gallery Image'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                )}
                {item.caption && (
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] py-1 px-2 text-center backdrop-blur-xs">
                    {lang === 'mr' ? transliterateToMarathi(item.caption) : item.caption}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Invitation Hosts Footer */}
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="relative z-10 mt-10 text-center max-w-md px-6 space-y-4"
      >
        <p className="text-xs md:text-sm text-[#78350F] font-bold whitespace-pre-line leading-relaxed font-sans">
          {getHosts()}
        </p>
        <div className="w-16 h-[1px] bg-[#E9E4D9] mx-auto my-3" />
        <p className="text-xs italic text-[#5C4D3C] font-semibold">
          {t.concludesWith}
        </p>
      </motion.div>

      {/* RSVP */}
      <div className="relative z-10 mt-8 w-full max-w-sm">
        <RsvpForm
          invitationId={invitationId}
          isLive={isLive}
          externalLink={data.rsvpLink && data.rsvpLink !== '#' ? data.rsvpLink : null}
        />
      </div>

      {/* Branded Footer & Partner Attribution */}
      {!partner?.enabled && (
        <div className="relative z-10 mt-16 text-center space-y-2 border-t border-[#E9E4D9]/40 pt-8 w-full max-w-sm">
          {!data.isPremium && (
            <a 
              href="https://invitecloud.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#5C4D3C]/60 hover:text-[#B91C1C] transition-colors"
            >
              <img src="/assets/logo-mark.svg" alt="InviteCloud Logo" className="w-4 h-4 opacity-70" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              <span>Created with <strong className="font-bold text-[#78350F]">invitecloud.in</strong></span>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: MarathiWeddingInteractive });
